import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { getLocationFilterOptions } from '../../api/services/Locations';

export const RIDES_LOCATIONS_QUERY_KEY = 'rides-locations';

export const LOCATIONS_PER_PAGE = 10;

interface UseRidesLocationsParams {
  enabled: boolean;
  query?: string;
}

export const useRidesLocations = ({ enabled, query }: UseRidesLocationsParams) => {
  const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [RIDES_LOCATIONS_QUERY_KEY, query],
    queryFn: async ({ pageParam, signal }) => {
      const response = await getLocationFilterOptions(
        { page: pageParam, perPage: LOCATIONS_PER_PAGE, ...(query && { q: query }) },
        signal
      );

      if (response.type === 'error') {
        throw new Error(response.error.errors[0] ?? 'Failed to load locations');
      }

      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const fetchedCount = allPages.reduce((count, page) => count + (page.locations?.length ?? 0), 0);
      const totalCount = lastPage.meta?.totalCount ?? fetchedCount;

      return fetchedCount < totalCount ? allPages.length + 1 : undefined;
    },
    enabled,
  });

  const { data: baselineData } = useQuery({
    queryKey: [RIDES_LOCATIONS_QUERY_KEY, 'baseline'],
    queryFn: async ({ signal }) => {
      const response = await getLocationFilterOptions({ page: 1, perPage: LOCATIONS_PER_PAGE }, signal);

      if (response.type === 'error') {
        throw new Error(response.error.errors[0] ?? 'Failed to load locations');
      }

      return response.data;
    },
    enabled: enabled && !!query,
  });

  const locations = useMemo(() => data?.pages.flatMap((page) => page.locations ?? []) ?? [], [data]);
  const totalCount = data?.pages[0]?.meta?.totalCount ?? locations.length;
  const baselineTotalCount = baselineData?.meta?.totalCount ?? totalCount;

  return {
    locations,
    totalCount,
    baselineTotalCount,
    isFetching,
    hasMore: hasNextPage ?? false,
    fetchNextPage,
  };
};
